import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Select,
  Input,
  Textarea,
  Switch,
  FormLabel,
  FormControl,
  Divider,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  Progress,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Code,
  useToast,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { 
  FiUpload, 
  FiPlay, 
  FiRefreshCw, 
  FiSettings, 
  FiMonitor,
  FiCopy,
  FiTrash2,
  FiMessageSquare,
  FiZap,
  FiClock,
  FiActivity,
  FiToggleLeft,
  FiToggleRight
} from 'react-icons/fi';
import { useUser, useFindMany, useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';

const AgentsTestingInterface = () => {
  // ALL HOOKS MUST BE AT THE TOP - React Rules of Hooks
  const user = useUser();
  const toast = useToast();

  // Fetch assistants (excluding Amy)
  const [{ data: assistantsData, fetching: assistantsLoading, error: assistantsError }] = useFindMany(api.assistant, {
    filter: {
      name: { notEquals: 'Amy' }
    },
    select: {
      id: true,
      name: true,
      description: true,
      assistantType: true,
      openAiId: true,
      allowFileUploads: true,
      processingCapabilities: true
    }
  });

  // State management
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const [selectedAgentType, setSelectedAgentType] = useState('don');
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4000);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [enabledTools, setEnabledTools] = useState({
    fileAnalysis: true,
    documentGeneration: true,
    dataRetrieval: true,
    workflows: true
  });
  const [sessionPersistence, setSessionPersistence] = useState(true);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [toolCalls, setToolCalls] = useState([]);
  const [processingStatus, setProcessingStatus] = useState('idle');

  const [useRealAgents, setUseRealAgents] = useState(true);
  const [lastAgentResult, setLastAgentResult] = useState(null);

  // Refs
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const sessionInitializedRef = useRef(false);

  // Global actions
  const [testResult, testAgent] = useGlobalAction(api.testAssistantAccess);
  const [createThreadResult, createThread] = useGlobalAction(api.globalCreateThread);
  const [sendMessageResult, sendMessage] = useGlobalAction(api.globalCreateMessage);
  const [agentSDKResult, runAgentSDK] = useGlobalAction(api.testAgentsSDK);

  // Generate new session ID
  const generateSessionId = () => {
    const id = `test_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(id);
    setMessages([]);
    setToolCalls([]);
    setDebugInfo({});
    setPerformanceMetrics({});
    toast({
      title: 'New Session Started',
      description: `Session ID: ${id}`,
      status: 'success',
      duration: 3000
    });
  };

  // Initialize session on component mount
  useEffect(() => {
    if (!sessionId && !sessionInitializedRef.current) {
      sessionInitializedRef.current = true;
      generateSessionId();
    }
  }, []);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Access control - check if user has admin privileges (same as admin dashboard)
  const checkIsAdmin = (user) => {
    if (!user) return false;
    
    // Special email check for backwards compatibility
    if (user?.email === '74spartan@gmail.com') {
      return true;
    }
    
    // Check if user has roles
    if (!user?.roles || !Array.isArray(user.roles)) {
      return false;
    }
    
    // Define admin role names to check for
    const adminRoleNames = ['admin', 'sysadmin', 'system-admin'];
    
    // Check roles - handle both string and object formats
    return user.roles.some(role => {
      if (typeof role === 'string') {
        // Role is a string, check directly
        return adminRoleNames.includes(role);
      } else if (typeof role === 'object' && role !== null) {
        // Role is an object, check key and name properties
        return adminRoleNames.includes(role.key) || adminRoleNames.includes(role.name);
      }
      return false;
    });
  };

  const isAdmin = checkIsAdmin(user);

  // Show loading state while fetching user or assistants
  if (!user || assistantsLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading...</Text>
      </Box>
    );
  }

  // Access denied component
  if (!isAdmin) {
    return (
      <Box p={8} textAlign="center">
        <Alert status="error" maxW="md" mx="auto">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Access Denied</Text>
            <Text>This interface is restricted to system administrators only.</Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      toast({
        title: 'File Uploaded',
        description: `${file.name} (${(file.size / 1024).toFixed(1)} KB)`,
        status: 'success',
        duration: 3000
      });
    }
  };

  // Trigger greeting/initiator message for current session
  const handleTriggerGreeting = async () => {
    if (!sessionId) {
      toast({
        title: 'No Session',
        description: 'Please generate a new session first',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    setIsStreaming(true);
    setProcessingStatus('processing');

    const startTime = Date.now();

    try {
      console.log('🎉 Triggering greeting message for agent:', selectedAgentType);
      
      const result = await runAgentSDK({
        agentType: selectedAgentType,
        input: '', // Empty input for initiator
        isInitiatorMessage: true, // The key flag that triggers greeting
        settings: {
          model: selectedModel,
          temperature,
          maxTokens,
          enabledTools
        },
        sessionId: sessionPersistence ? sessionId : undefined
      });

      console.log('🎉 Greeting result:', result);
      setLastAgentResult(result);

      // Check multiple possible response structures
      const isSuccess = result?.success === true || result?.data?.success === true;
      const responseContent = result?.response || result?.data?.response || result?.data?.content;
      const errorMessage = result?.error || result?.data?.error || result?.message;

      if (isSuccess && responseContent) {
        const assistantMessage = {
          id: Date.now(),
          role: 'assistant',
          content: responseContent,
          timestamp: new Date().toISOString(),
          toolCalls: result?.toolCalls || result?.data?.toolCalls || [],
          metadata: {
            agentUsed: result?.agentUsed || result?.data?.agentUsed,
            agentName: result?.agentName || result?.data?.agentName,
            processingTime: result?.processingTime || result?.data?.processingTime,
            sessionContext: result?.sessionContext || result?.data?.sessionContext,
            isGreeting: true,
            ...(result?.debugInfo || result?.data?.debugInfo || {})
          },
          agentType: selectedAgentType
        };

        console.log('✅ Creating greeting message:', assistantMessage);
        setMessages(prev => [...prev, assistantMessage]);

        // Update performance metrics
        const endTime = Date.now();
        setPerformanceMetrics({
          responseTime: result?.totalProcessingTime || result?.data?.totalProcessingTime || endTime - startTime,
          tokensUsed: result?.usage?.total_tokens || result?.data?.usage?.total_tokens || 0,
          toolCallsExecuted: (result?.toolCalls || result?.data?.toolCalls || []).length,
          compressionRatio: 0,
          agentType: result?.agentUsed || result?.data?.agentUsed,
          success: true,
          greeting: true
        });

        toast({
          title: 'Greeting Generated',
          description: `${selectedAgentType.toUpperCase()} introduced themselves successfully`,
          status: 'success',
          duration: 3000
        });

      } else {
        const finalErrorMessage = errorMessage || 'Failed to generate greeting';
        
        const assistantMessage = {
          id: Date.now(),
          role: 'assistant',
          content: `❌ Greeting Error: ${finalErrorMessage}`,
          timestamp: new Date().toISOString(),
          toolCalls: [],
          metadata: { error: true, greeting: true },
          agentType: selectedAgentType
        };

        setMessages(prev => [...prev, assistantMessage]);
        
        toast({
          title: 'Greeting Failed',
          description: finalErrorMessage,
          status: 'error',
          duration: 5000
        });
      }

    } catch (error) {
      console.error('Greeting Error:', error);
      
      const assistantMessage = {
        id: Date.now(),
        role: 'assistant',
        content: `❌ System Error: ${error.message}`,
        timestamp: new Date().toISOString(),
        toolCalls: [],
        metadata: { error: true, systemError: true, greeting: true }
      };

      setMessages(prev => [...prev, assistantMessage]);

      toast({
        title: 'System Error',
        description: error.message,
        status: 'error',
        duration: 5000
      });

    } finally {
      setIsStreaming(false);
      setProcessingStatus('idle');
    }
  };

  // Send message with real agents SDK or simulation
  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString(),
      file: uploadedFile?.name
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsStreaming(true);
    setProcessingStatus('processing');

    const startTime = Date.now();

    try {
      if (useRealAgents) {
        // Call real Agents SDK
        console.log('🚀 Calling runAgentSDK with params:', {
          agentType: selectedAgentType,
          input: currentMessage,
          sessionId: sessionPersistence ? sessionId : undefined
        });
        
        const result = await runAgentSDK({
          agentType: selectedAgentType,
          input: currentMessage,
          settings: {
            model: selectedModel,
            temperature,
            maxTokens,
            enabledTools
          },
          sessionId: sessionPersistence ? sessionId : undefined,
          uploadedFile: uploadedFile ? {
            name: uploadedFile.name,
            size: uploadedFile.size,
            type: uploadedFile.type
          } : undefined
        });

        console.log('📥 Raw result from runAgentSDK:', result);
        console.log('🔍 result.success:', result.success);
        console.log('🔍 result.response:', result.response);
        console.log('🔍 result.error:', result.error);
        console.log('🔍 Full result keys:', Object.keys(result || {}));

        setLastAgentResult(result);

        // Check multiple possible response structures
        const isSuccess = result?.success === true || result?.data?.success === true;
        const responseContent = result?.response || result?.data?.response || result?.data?.content;
        const errorMessage = result?.error || result?.data?.error || result?.message;

        console.log('🎯 Computed values:');
        console.log('  - isSuccess:', isSuccess);
        console.log('  - responseContent:', responseContent);
        console.log('  - errorMessage:', errorMessage);

        if (isSuccess && responseContent) {
          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: responseContent,
            timestamp: new Date().toISOString(),
            toolCalls: result?.toolCalls || result?.data?.toolCalls || [],
            metadata: {
              agentUsed: result?.agentUsed || result?.data?.agentUsed,
              agentName: result?.agentName || result?.data?.agentName,
              processingTime: result?.processingTime || result?.data?.processingTime,
              sessionContext: result?.sessionContext || result?.data?.sessionContext,
              ...(result?.debugInfo || result?.data?.debugInfo || {})
            },
            agentType: selectedAgentType
          };

          console.log('✅ Creating success message:', assistantMessage);
          setMessages(prev => [...prev, assistantMessage]);

          // Update tool calls
          const toolCalls = result?.toolCalls || result?.data?.toolCalls;
          if (toolCalls) {
            setToolCalls(prev => [...prev, ...toolCalls]);
          }

          // Update debug info
          setDebugInfo(result?.debugInfo || result?.data?.debugInfo || {});

          // Update performance metrics
          const endTime = Date.now();
          setPerformanceMetrics({
            responseTime: result?.totalProcessingTime || result?.data?.totalProcessingTime || endTime - startTime,
            tokensUsed: result?.usage?.total_tokens || result?.data?.usage?.total_tokens || 0,
            toolCallsExecuted: (result?.toolCalls || result?.data?.toolCalls || []).length,
            compressionRatio: 0,
            agentType: result?.agentUsed || result?.data?.agentUsed,
            success: true
          });

        } else {
          // Handle errors from agents SDK
          const finalErrorMessage = errorMessage || 'Agent failed to respond - unknown error';
          
          console.log('❌ Creating error message:', finalErrorMessage);
          
          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: `❌ Error: ${finalErrorMessage}`,
            timestamp: new Date().toISOString(),
            toolCalls: [],
            metadata: { error: true },
            agentType: selectedAgentType
          };

          setMessages(prev => [...prev, assistantMessage]);
          
          toast({
            title: 'Agent Error',
            description: finalErrorMessage,
            status: 'error',
            duration: 5000
          });
        }

      } else {
        // Fallback to simulation mode
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          toolCalls: [],
          metadata: { simulation: true }
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Simulate streaming chunks
        const response = `🤖 Simulated response from ${selectedAgentType.toUpperCase()} agent.

This is a fallback simulation mode. To test real agents:
1. Enable "Use Real Agents" toggle
2. Select agent type: ${selectedAgentType}
3. Try secret codes: 8675309 or 8657738922
4. Current settings:
   - Model: ${selectedModel}
   - Temperature: ${temperature}
   - Max Tokens: ${maxTokens}
   - Enabled Tools: ${Object.entries(enabledTools).filter(([_, enabled]) => enabled).map(([tool]) => tool).join(', ')}
   ${uploadedFile ? `- Uploaded File: ${uploadedFile.name}` : ''}`;

        // Stream the response
        for (let i = 0; i < response.length; i += 10) {
          await new Promise(resolve => setTimeout(resolve, 30));
          const chunk = response.slice(0, i + 10);
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessage.id 
              ? { ...msg, content: chunk }
              : msg
          ));
        }

        // Simulate tool calls
        const mockToolCalls = [
          {
            id: 'sim_call_1',
            type: 'function',
            function: {
              name: `${selectedAgentType}_analyze`,
              arguments: JSON.stringify({ 
                message: currentMessage,
                file: uploadedFile?.name || null
              })
            },
            result: `${selectedAgentType.toUpperCase()} simulation complete`
          }
        ];

        setToolCalls(prev => [...prev, ...mockToolCalls]);

        // Update performance metrics
        const endTime = Date.now();
        setPerformanceMetrics({
          responseTime: endTime - startTime,
          tokensUsed: Math.floor(Math.random() * 500) + 100,
          toolCallsExecuted: mockToolCalls.length,
          compressionRatio: 0.85,
          agentType: selectedAgentType,
          success: true,
          simulation: true
        });
      }

    } catch (error) {
      console.error('Agent SDK Error:', error);
      
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: `❌ System Error: ${error.message}`,
        timestamp: new Date().toISOString(),
        toolCalls: [],
        metadata: { error: true, systemError: true }
      };

      setMessages(prev => [...prev, assistantMessage]);

      toast({
        title: 'System Error',
        description: error.message,
        status: 'error',
        duration: 5000
      });

      // Update performance metrics with error
      const endTime = Date.now();
      setPerformanceMetrics(prev => ({
        ...prev,
        responseTime: endTime - startTime,
        success: false,
        error: error.message
      }));

    } finally {
      setIsStreaming(false);
      setProcessingStatus('idle');
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Box p={6} maxW="100%" mx="auto">
      {/* Header */}
      <VStack spacing={4} align="stretch" mb={8}>
        <HStack justify="space-between">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="blue.600">
              🧪 Advanced Agents SDK Testing Interface
            </Heading>
            <Text color="gray.600" fontSize="sm">
              System Administrator Development Environment
            </Text>
          </VStack>
          <HStack spacing={2}>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="bold">Mode:</Text>
              <Button
                size="sm"
                leftIcon={useRealAgents ? <FiToggleRight /> : <FiToggleLeft />}
                colorScheme={useRealAgents ? "green" : "orange"}
                variant="outline"
                onClick={() => setUseRealAgents(!useRealAgents)}
              >
                {useRealAgents ? 'Real Agents' : 'Simulation'}
              </Button>
            </HStack>
            <Badge colorScheme="red" variant="solid" p={2}>
              TESTING ENVIRONMENT
            </Badge>
          </HStack>
        </HStack>

        {/* Session Info */}
        <Card>
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="bold">Current Session:</Text>
                <Code fontSize="xs">{sessionId}</Code>
              </VStack>
              <Button 
                leftIcon={<FiRefreshCw />} 
                size="sm" 
                colorScheme="blue"
                onClick={generateSessionId}
              >
                New Session
              </Button>
            </HStack>
          </CardBody>
        </Card>
      </VStack>

      <Tabs variant="line" colorScheme="blue">
        <TabList>
          <Tab>💬 Chat Interface</Tab>
          <Tab>⚖️ Comparison View</Tab>
          <Tab>🛠️ Testing Controls</Tab>
          <Tab>🔍 Debug Information</Tab>
          <Tab>📊 Performance Metrics</Tab>
        </TabList>

        <TabPanels>
          {/* Chat Interface Tab */}
          <TabPanel>
            <Grid templateColumns="1fr 300px" gap={6}>
              {/* Main Chat Area */}
              <GridItem>
                <VStack spacing={4} align="stretch">
                  {/* Agent Type Selection */}
                  <Grid templateColumns="1fr 1fr" gap={4}>
                    <FormControl>
                      <FormLabel>Agent Type (Agents SDK)</FormLabel>
                      <Select 
                        value={selectedAgentType} 
                        onChange={(e) => setSelectedAgentType(e.target.value)}
                      >
                        <option value="don">DON - Clinical Assistant</option>
                        <option value="maia">MAIA - Medical AI Assistant</option>
                        <option value="gerald">GERALD - General Assistant</option>
                        <option value="custom">Custom Agent</option>
                      </Select>
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>Fallback Assistant (Legacy)</FormLabel>
                      <Select 
                        value={selectedAssistant} 
                        onChange={(e) => setSelectedAssistant(e.target.value)}
                        placeholder="Choose fallback assistant..."
                        isDisabled={useRealAgents}
                      >
                        {(assistantsData || []).map(assistant => (
                          <option key={assistant.id} value={assistant.id}>
                            {assistant.name} ({assistant.assistantType})
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Greeting Trigger */}
                  <HStack spacing={4} justify="center" p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                    <VStack spacing={2}>
                      <Text fontSize="sm" fontWeight="bold" color="purple.700">
                        🎉 Test Agent Greeting Flow
                      </Text>
                      <Button
                        leftIcon={<FiMessageSquare />}
                        size="sm"
                        colorScheme="purple"
                        variant="outline"
                        onClick={handleTriggerGreeting}
                        isLoading={isStreaming}
                        disabled={!selectedAgentType || !useRealAgents}
                      >
                        Trigger {selectedAgentType.toUpperCase()} Greeting
                      </Button>
                      <Text fontSize="xs" color="gray.600" textAlign="center">
                        Simulates the automatic greeting when a user first selects this agent
                      </Text>
                    </VStack>
                  </HStack>

                  {/* Chat Messages */}
                  <Card h="400px">
                    <CardBody p={4} overflowY="auto">
                      <VStack spacing={3} align="stretch">
                        {messages.map((message) => (
                          <Box
                            key={message.id}
                            align={message.role === 'user' ? 'end' : 'start'}
                            textAlign={message.role === 'user' ? 'right' : 'left'}
                          >
                            <Box>
                              <Box
                                bg={message.role === 'user' ? 'blue.500' : 'white'}
                                color={message.role === 'user' ? 'white' : 'gray.800'}
                                border={message.role === 'user' ? 'none' : '1px solid'}
                                borderColor={message.role === 'user' ? 'transparent' : 'gray.200'}
                                p={3}
                                borderRadius="lg"
                                maxW="80%"
                                display="inline-block"
                                shadow={message.role === 'user' ? 'none' : 'sm'}
                              >
                                <Text whiteSpace="pre-wrap" color={message.role === 'user' ? 'white' : 'gray.800'}>
                                  {message.content}
                                </Text>
                                {message.file && (
                                  <Badge mt={2} colorScheme="green">
                                    📎 {message.file}
                                  </Badge>
                                )}
                              </Box>
                              {message.agentType && (
                                <Badge size="sm" colorScheme="purple" mt={1}>
                                  {message.agentType.toUpperCase()}
                                </Badge>
                              )}
                              {message.metadata?.simulation && (
                                <Badge size="sm" colorScheme="orange" mt={1} ml={1}>
                                  SIMULATED
                                </Badge>
                              )}
                              {message.metadata?.error && (
                                <Badge size="sm" colorScheme="red" mt={1} ml={1}>
                                  ERROR
                                </Badge>
                              )}
                            </Box>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {new Date(message.timestamp).toLocaleTimeString()}
                            </Text>
                          </Box>
                        ))}
                        {isStreaming && (
                          <HStack spacing={2}>
                            <Spinner size="sm" />
                            <Text fontSize="sm" color="gray.500">Assistant is typing...</Text>
                          </HStack>
                        )}
                        <div ref={chatEndRef} />
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Message Input */}
                  <HStack spacing={2}>
                    <Input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      accept=".pdf,.doc,.docx,.txt,.json"
                    />
                    <Button
                      leftIcon={<FiUpload />}
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                    <Input
                      value={currentMessage}
                      onChange={(e) => setCurrentMessage(e.target.value)}
                      placeholder="Type your message..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <Button
                      leftIcon={<FiPlay />}
                      colorScheme="blue"
                      onClick={handleSendMessage}
                      isLoading={isStreaming}
                      disabled={!currentMessage.trim()}
                    >
                      {useRealAgents ? 'Send to Agent' : 'Simulate'}
                    </Button>
                  </HStack>

                  {uploadedFile && (
                    <Box bg="green.50" p={3} borderRadius="md" border="1px solid" borderColor="green.200">
                      <HStack justify="space-between">
                        <Text fontSize="sm">
                          📎 {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                        </Text>
                        <IconButton
                          size="sm"
                          icon={<FiTrash2 />}
                          onClick={() => setUploadedFile(null)}
                        />
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </GridItem>

              {/* Side Panel */}
              <GridItem>
                <VStack spacing={4} align="stretch">
                  {/* Processing Status */}
                  <Card>
                    <CardHeader pb={2}>
                      <Text fontWeight="bold" fontSize="sm">Processing Status</Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={2} align="stretch">
                        <HStack>
                          <Badge colorScheme={processingStatus === 'idle' ? 'green' : 'blue'}>
                            {processingStatus}
                          </Badge>
                          {processingStatus === 'processing' && <Spinner size="xs" />}
                        </HStack>
                        {processingStatus === 'processing' && (
                          <Progress size="sm" isIndeterminate colorScheme="blue" />
                        )}
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Tool Calls */}
                  <Card>
                    <CardHeader pb={2}>
                      <Text fontWeight="bold" fontSize="sm">Recent Tool Calls</Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={2} align="stretch">
                        {toolCalls.slice(-3).map((call, index) => (
                          <Box key={call.id || index} p={2} bg="gray.50" borderRadius="md">
                            <Text fontSize="xs" fontWeight="bold">{call.function?.name}</Text>
                            <Text fontSize="xs" color="gray.600">{call.result}</Text>
                            {call.id?.startsWith('sim_') && (
                              <Badge size="xs" colorScheme="orange">SIM</Badge>
                            )}
                          </Box>
                        ))}
                        {toolCalls.length === 0 && (
                          <Text fontSize="sm" color="gray.500">No tool calls yet</Text>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Agent SDK Status */}
                  <Card>
                    <CardHeader pb={2}>
                      <Text fontWeight="bold" fontSize="sm">Agent SDK Status</Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={2} align="stretch">
                        <HStack justify="space-between">
                          <Text fontSize="sm">Mode:</Text>
                          <Badge colorScheme={useRealAgents ? "green" : "orange"}>
                            {useRealAgents ? 'Real' : 'Sim'}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm">Agent:</Text>
                          <Text fontSize="sm" fontWeight="bold">{selectedAgentType.toUpperCase()}</Text>
                        </HStack>
                        {lastAgentResult && (
                          <HStack justify="space-between">
                            <Text fontSize="sm">Last Result:</Text>
                            <Badge colorScheme={lastAgentResult.success ? "green" : "red"}>
                              {lastAgentResult.success ? 'Success' : 'Failed'}
                            </Badge>
                          </HStack>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              </GridItem>
            </Grid>
          </TabPanel>

          {/* Comparison View Tab */}
          <TabPanel>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Heading size="md">Side-by-Side Comparison</Heading>
                <Switch
                  isChecked={comparisonMode}
                  onChange={(e) => setComparisonMode(e.target.checked)}
                >
                  <Text ml={2}>Enable Comparison Mode</Text>
                </Switch>
              </HStack>

              {comparisonMode ? (
                <Grid templateColumns="1fr 1fr" gap={6}>
                  <Card>
                    <CardHeader>
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Legacy Assistants</Text>
                        <Badge colorScheme="blue">Current System</Badge>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={3} align="stretch">
                        <FormControl>
                          <FormLabel>Select Assistant</FormLabel>
                          <Select 
                            value={selectedAssistant} 
                            onChange={(e) => setSelectedAssistant(e.target.value)}
                            placeholder="Choose an assistant..."
                          >
                            {(assistantsData || []).map(assistant => (
                              <option key={assistant.id} value={assistant.id}>
                                {assistant.name} ({assistant.assistantType})
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </VStack>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader>
                      <Text fontWeight="bold">Agent B (New SDK)</Text>
                    </CardHeader>
                    <CardBody>
                      <Text color="gray.500">New agents SDK implementation</Text>
                    </CardBody>
                  </Card>
                </Grid>
              ) : (
                <Card>
                  <CardBody textAlign="center" py={12}>
                    <Text color="gray.500" fontSize="lg">
                      Enable comparison mode to start side-by-side testing
                    </Text>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </TabPanel>

          {/* Testing Controls Tab */}
          <TabPanel>
            <Grid templateColumns="1fr 1fr" gap={6}>
              {/* Model Settings */}
              <Card>
                <CardHeader>
                  <Text fontWeight="bold">Model Configuration</Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>Model</FormLabel>
                      <Select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                        <option value="gpt-4o">GPT-4o</option>
                        <option value="gpt-4o-mini">GPT-4o Mini</option>
                        <option value="o1-preview">O1 Preview</option>
                        <option value="o1-mini">O1 Mini</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Temperature: {temperature}</FormLabel>
                      <Slider
                        value={temperature}
                        onChange={(val) => setTemperature(val)}
                        min={0}
                        max={2}
                        step={0.1}
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Max Tokens</FormLabel>
                      <NumberInput
                        value={maxTokens}
                        onChange={(val) => setMaxTokens(parseInt(val))}
                        min={100}
                        max={4000}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {/* Tool Configuration */}
              <Card>
                <CardHeader>
                  <Text fontWeight="bold">Tool Configuration</Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    {Object.entries(enabledTools).map(([tool, enabled]) => (
                      <HStack key={tool} justify="space-between">
                        <Text textTransform="capitalize">{tool.replace(/([A-Z])/g, ' $1')}</Text>
                        <Switch
                          isChecked={enabled}
                          onChange={(e) => setEnabledTools(prev => ({
                            ...prev,
                            [tool]: e.target.checked
                          }))}
                        />
                      </HStack>
                    ))}

                    <Divider />

                    <HStack justify="space-between">
                      <Text>Session Persistence</Text>
                      <Switch
                        isChecked={sessionPersistence}
                        onChange={(e) => setSessionPersistence(e.target.checked)}
                      />
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </Grid>
          </TabPanel>

          {/* Debug Information Tab */}
          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Accordion allowMultiple>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Assistant Configuration
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <pre style={{ fontSize: '14px', background: '#f7fafc', padding: '12px', borderRadius: '6px', overflow: 'auto' }}>
                      {JSON.stringify((assistantsData || []).find(a => a.id === selectedAssistant) || {}, null, 2)}
                    </pre>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Tool Calls History
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <pre style={{ fontSize: '14px', background: '#f7fafc', padding: '12px', borderRadius: '6px', overflow: 'auto' }}>
                      {JSON.stringify(toolCalls, null, 2)}
                    </pre>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Session Metadata
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <pre style={{ fontSize: '14px', background: '#f7fafc', padding: '12px', borderRadius: '6px', overflow: 'auto' }}>
                      {JSON.stringify({
                        sessionId,
                        selectedAssistant,
                        selectedModel,
                        temperature,
                        maxTokens,
                        enabledTools,
                        sessionPersistence
                      }, null, 2)}
                    </pre>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          </TabPanel>

          {/* Performance Metrics Tab */}
          <TabPanel>
            <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
              <Card>
                <CardHeader>
                  <HStack>
                    <FiClock />
                    <Text fontWeight="bold">Response Time</Text>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                    {performanceMetrics.responseTime || 0}ms
                  </Text>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <HStack>
                    <FiZap />
                    <Text fontWeight="bold">Tokens Used</Text>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                    {performanceMetrics.tokensUsed || 0}
                  </Text>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <HStack>
                    <FiSettings />
                    <Text fontWeight="bold">Tool Calls</Text>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                    {performanceMetrics.toolCallsExecuted || 0}
                  </Text>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <HStack>
                    <FiActivity />
                    <Text fontWeight="bold">Efficiency</Text>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                    {Math.round((performanceMetrics.compressionRatio || 0) * 100)}%
                  </Text>
                </CardBody>
              </Card>
            </Grid>

            <Card mt={6}>
              <CardHeader>
                <Text fontWeight="bold">Performance History</Text>
              </CardHeader>
              <CardBody>
                <Text color="gray.500" textAlign="center" py={8}>
                  Performance charts and historical data would be displayed here in a production environment
                </Text>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default AgentsTestingInterface;